import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { format, startOfMonth, getISOWeek } from "date-fns";
import { DollarSign, Users, TrendingUp, BarChart2, Table2, Download, ChevronLeft, ChevronRight, AlertTriangle, Coins, Wallet, Radio, ChevronDown, EyeOff } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from "recharts";
import { DateRangePicker } from "@/components/DateRangePicker";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16", "#f97316", "#6366f1"];

function getWeekKey(ymd) {
  const d = new Date(ymd + "T00:00:00");
  const week = getISOWeek(d);
  const year = d.getFullYear();
  return `${year}-W${String(week).padStart(2, "0")}`;
}

function getMonthKey(ymd) {
  return ymd.slice(0, 7);
}

function aggregateByTime(data, granularity, valueKeys, avgKeys = []) {
  if (granularity === "day") return data;

  const groups = {};
  for (const row of data) {
    const key = granularity === "week" ? getWeekKey(row.date || row.ymd) : getMonthKey(row.date || row.ymd);
    if (!groups[key]) groups[key] = { _key: key, _count: 0 };
    groups[key]._count += 1;
    for (const vk of [...valueKeys, ...avgKeys]) {
      groups[key][vk] = (groups[key][vk] || 0) + (row[vk] || 0);
    }
  }

  return Object.values(groups)
    .sort((a, b) => a._key.localeCompare(b._key))
    .map((g) => {
      const result = { date: g._key };
      for (const vk of valueKeys) result[vk] = Math.round(g[vk] * 100) / 100;
      for (const ak of avgKeys) result[ak] = Math.round(g[ak] / g._count);
      return result;
    });
}

function buildBreakdownData(tableData, granularity, breakdown, metric) {
  // metric: "earnings" or "peers"
  // Group by (time, entity)
  const groups = {};
  const entities = new Set();

  for (const row of tableData) {
    const timeKey = granularity === "day" ? row.ymd
      : granularity === "week" ? getWeekKey(row.ymd)
      : getMonthKey(row.ymd);

    const entity = breakdown === "campaign" ? row.campaign : row.partner_name;
    if (!entity || entity === "Unassigned") continue;
    entities.add(entity);

    if (!groups[timeKey]) groups[timeKey] = { date: timeKey };
    const value = metric === "earnings" ? row.raw_earnings : row.peers;
    groups[timeKey][entity] = (groups[timeKey][entity] || 0) + (value || 0);
  }

  const sortedData = Object.values(groups).sort((a, b) => a.date.localeCompare(b.date));

  // For DAU with week/month, average the peers
  if (metric === "peers" && granularity !== "day") {
    const dayCounts = {};
    for (const row of tableData) {
      const timeKey = granularity === "week" ? getWeekKey(row.ymd) : getMonthKey(row.ymd);
      if (!dayCounts[timeKey]) dayCounts[timeKey] = new Set();
      dayCounts[timeKey].add(row.ymd);
    }
    for (const item of sortedData) {
      const numDays = dayCounts[item.date]?.size || 1;
      for (const entity of entities) {
        if (item[entity]) item[entity] = Math.round(item[entity] / numDays);
      }
    }
  }

  return { data: sortedData, entities: [...entities] };
}

// "By Partner" payout view — what each partner is actually paid (SL partner_earnings + CP cp_earnings).
function buildPartnerPayoutData(tableData, granularity) {
  const groups = {};
  const entities = new Set();
  for (const row of tableData) {
    const timeKey = granularity === "day" ? row.ymd
      : granularity === "week" ? getWeekKey(row.ymd)
      : getMonthKey(row.ymd);
    if (!groups[timeKey]) groups[timeKey] = { date: timeKey };
    if (row.partner_name && row.partner_name !== "Unassigned" && (row.partner_earnings || 0) !== 0) {
      entities.add(row.partner_name);
      groups[timeKey][row.partner_name] = (groups[timeKey][row.partner_name] || 0) + (row.partner_earnings || 0);
    }
    if (row.cp_name && (row.cp_earnings || 0) !== 0) {
      const label = `${row.cp_name} (CP)`;
      entities.add(label);
      groups[timeKey][label] = (groups[timeKey][label] || 0) + (row.cp_earnings || 0);
    }
  }
  const sortedData = Object.values(groups).sort((a, b) => a.date.localeCompare(b.date));
  return { data: sortedData, entities: [...entities] };
}

export default function AdminDashboard({ api = adminApi, isProduct = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(() => new Date());
  const [campaignFilter, setCampaignFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [appFilter, setAppFilter] = useState("all");
  const [campaigns, setCampaigns] = useState([]);
  const [sources, setSources] = useState([]);
  const [apps, setApps] = useState([]);
  const [viewMode, setViewMode] = useState("chart");
  const [availableDates, setAvailableDates] = useState([]);
  const [granularity, setGranularity] = useState("day");
  const [breakdown, setBreakdown] = useState("payout");
  const [tablePage, setTablePage] = useState(0);
  const TABLE_PAGE_SIZE = 50;
  const [unattributed, setUnattributed] = useState(null);
  const [unattributedExpanded, setUnattributedExpanded] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = format(startDate, "yyyy-MM-dd");
      if (endDate) params.end_date = format(endDate, "yyyy-MM-dd");
      if (campaignFilter && campaignFilter !== "all") params.campaign_id = campaignFilter;
      if (sourceFilter && sourceFilter !== "all") params.source = sourceFilter;
      if (appFilter && appFilter !== "all") params.app_uuid = appFilter;      const [dashRes, unattribRes] = await Promise.all([
        api.getDashboard(params),
        api.getUnattributedAnalysis({ start_date: params.start_date, end_date: params.end_date }).catch(() => null),
      ]);
      setData(dashRes.data);
      setUnattributed(unattribRes?.data || null);
    } catch (err) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, campaignFilter, sourceFilter, appFilter]);

  const fetchCampaigns = useCallback(async () => {
    try { const res = await api.getCampaigns(); setCampaigns(res.data); } catch (_) {}
  }, []);

  const fetchSources = useCallback(async () => {
    try { const res = await api.getSources(); setSources(res.data); } catch (_) {}
  }, []);

  const fetchApps = useCallback(async () => {
    try { const res = await api.getApps(); setApps(res.data); } catch (_) {}
  }, []);

  const fetchAvailableDates = useCallback(async () => {
    try {
      const params = {};
      if (campaignFilter && campaignFilter !== "all") params.campaign_id = campaignFilter;
      if (sourceFilter && sourceFilter !== "all") params.source = sourceFilter;
      const res = await api.getAvailableDates(params);
      setAvailableDates(res.data);
    } catch (_) {}
  }, [campaignFilter, sourceFilter]);

  useEffect(() => { fetchCampaigns(); fetchSources(); fetchApps(); }, [fetchCampaigns, fetchSources, fetchApps]);
  useEffect(() => { fetchAvailableDates(); }, [fetchAvailableDates]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const summary = data?.summary || {};
  const daily = data?.daily || [];
  const tableData = data?.table || [];
  const fx = data?.fx || {};
  const refRate = fx.reference_usd_inr;
  const [displayCurrency, setDisplayCurrency] = useState("USD");
  const inrView = displayCurrency === "INR" && !!refRate;
  // Money formatter for KPIs: base is USD; INR view multiplies by the reference rate (indicative).
  const m = (usd) => {
    const n = Number(usd || 0);
    if (inrView) return `₹${(n * refRate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const redacted = !!summary.web_indexing_redacted;
  const money = (v) => (v === null || v === undefined ? <span className="text-slate-400 italic font-sans text-xs" data-testid="cell-unavailable">Unavailable</span> : `$${v.toFixed(2)}`);
  const hasImpressions = (summary.total_impressions || 0) > 0;
  const hasPeers = (summary.avg_peers || 0) > 0;
  const isAdsOnly = sourceFilter === "fast_tv_ads" || sourceFilter === "springserve";
  const isDataOnly = sourceFilter === "web_indexing";
  const hasAds = hasImpressions && !isDataOnly;
  const costPath = (() => {
    const u = JSON.parse(localStorage.getItem("auth_user") || "{}");
    return u.role === "user" ? "/user/ad-costs" : "/admin/ad-costs";
  })();
  const cpPath = "/admin/users";

  // Impressions chart data
  const impressionsChartData = useMemo(() => {
    if (!hasImpressions) return [];
    const base = daily.filter(d => d.impressions > 0).map(d => ({ date: d.ymd, "Impressions": d.impressions }));
    return aggregateByTime(base, granularity, granularity === "day" ? ["Impressions"] : [], granularity !== "day" ? ["Impressions"] : []);
  }, [daily, granularity, hasImpressions]);

  // Compute chart data based on granularity + breakdown
  const revenueChartData = useMemo(() => {
    if (breakdown === "total") {
      const base = daily.map((d) => ({ date: d.ymd, "Total Revenue": d.earnings }));
      return aggregateByTime(base, granularity, ["Total Revenue"], []);
    }
    if (breakdown === "payout") {
      const base = daily.map((d) => ({ date: d.ymd, "Admin Earnings": d.admin_earnings, "SLDP Payouts": d.partner_earnings, "SLCP Payouts": d.cp_earnings || 0 }));
      return aggregateByTime(base, granularity, ["Admin Earnings", "SLDP Payouts", "SLCP Payouts"], []);
    }
    // By campaign or by partner
    if (breakdown === "partner") return buildPartnerPayoutData(tableData, granularity).data;
    return buildBreakdownData(tableData, granularity, breakdown, "earnings").data;
  }, [daily, tableData, granularity, breakdown]);

  const revenueEntities = useMemo(() => {
    if (breakdown === "total") return ["Total Revenue"];
    if (breakdown === "payout") return ["Admin Earnings", "SLDP Payouts", "SLCP Payouts"];
    if (breakdown === "partner") return buildPartnerPayoutData(tableData, granularity).entities;
    return buildBreakdownData(tableData, granularity, breakdown, "earnings").entities;
  }, [tableData, granularity, breakdown]);

  const dauChartData = useMemo(() => {
    if (breakdown === "total" || breakdown === "payout") {
      const base = daily.map((d) => ({ date: d.ymd, "Active Users": d.peers }));
      return aggregateByTime(base, granularity, [], ["Active Users"]);
    }
    return buildBreakdownData(tableData, granularity, breakdown, "peers").data;
  }, [daily, tableData, granularity, breakdown]);

  const dauEntities = useMemo(() => {
    if (breakdown === "total" || breakdown === "payout") return ["Active Users"];
    return buildBreakdownData(tableData, granularity, breakdown, "peers").entities;
  }, [tableData, granularity, breakdown]);

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-bold text-slate-900 tracking-tight whitespace-nowrap" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Dashboard
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangePicker startDate={startDate} endDate={endDate} onStartChange={setStartDate} onEndChange={setEndDate} availableDates={availableDates} isAdmin={true} />
          <Select value={campaignFilter} onValueChange={setCampaignFilter}>
            <SelectTrigger className="w-[150px] h-9 border-slate-200 text-sm" data-testid="campaign-filter">
              <SelectValue placeholder="All Identifiers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Identifiers</SelectItem>
              {campaigns.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-[150px] h-9 border-slate-200 text-sm" data-testid="source-filter">
              <SelectValue placeholder="All Sources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              {sources.map((s) => (<SelectItem key={s} value={s}>{s === "web_indexing" ? "Web-indexing" : s === "fast_tv_ads" ? "FAST TV Ads" : s === "springserve" ? "SpringServe" : s}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={appFilter} onValueChange={setAppFilter}>
            <SelectTrigger className="w-[160px] h-9 border-slate-200 text-sm" data-testid="app-filter">
              <SelectValue placeholder="All Apps" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Apps</SelectItem>
              {apps.map((a) => (<SelectItem key={a.app_uuid} value={a.app_uuid}>{a.app_name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Currency toggle + FX warnings */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2" data-testid="admin-currency-toggle">
          <span className="text-xs font-medium text-slate-500">Show totals in</span>
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
            <button onClick={() => setDisplayCurrency("USD")} className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${displayCurrency === "USD" ? "bg-blue-600 text-white" : "text-slate-500 hover:bg-slate-50"}`} data-testid="currency-usd-btn">$ USD</button>
            <button onClick={() => refRate ? setDisplayCurrency("INR") : toast.error("Set a reference USD→INR rate first (Statements page)")} className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${displayCurrency === "INR" ? "bg-blue-600 text-white" : "text-slate-500 hover:bg-slate-50"}`} data-testid="currency-inr-btn">₹ INR</button>
          </div>
          {inrView && <span className="text-[11px] text-amber-600" data-testid="inr-indicative-note">indicative @ ₹{refRate}/$ (reference rate)</span>}
        </div>
      </div>
      {(fx.unconverted_inr_partners || []).length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-700 flex items-center gap-2" data-testid="fx-missing-warning">
          <AlertTriangle size={14} /> No reference USD→INR rate set — INR payouts for {fx.unconverted_inr_partners.join(", ")} aren't converted, so totals may be off. Set the rate on the Statements page.
        </div>
      )}

      {redacted && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs text-slate-600 flex items-center gap-2" data-testid="web-indexing-redacted-banner">
          <EyeOff size={14} className="text-slate-500" />
          <span><b>All revenue figures exclude web-indexing.</b> Web-indexing activity (active users, identifiers) is shown; its earnings are unavailable for this role.</span>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="kpi-card animate-fade-in" data-testid="kpi-total-earnings">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <DollarSign size={20} className="text-blue-600" strokeWidth={1.5} />
            </div>
            <span className="kpi-label">Total Revenue</span>
          </div>
          <div className="kpi-value">{m(summary.total_earnings)}</div>
          {redacted && <p className="text-[11px] text-slate-400 mt-1" data-testid="kpi-excl-web-note">Excl. web-indexing</p>}
        </div>
        {redacted && (
          <div className="kpi-card animate-fade-in" data-testid="kpi-web-indexing-revenue">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <EyeOff size={20} className="text-slate-500" strokeWidth={1.5} />
              </div>
              <span className="kpi-label">Web-indexing Revenue</span>
            </div>
            <div className="kpi-value text-slate-400 text-xl" data-testid="kpi-web-indexing-unavailable">Unavailable</div>
          </div>
        )}
        {hasAds && (
          <div className="kpi-card animate-fade-in" data-testid="kpi-ad-cost">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center">
                <Coins size={20} className="text-rose-600" strokeWidth={1.5} />
              </div>
              <span className="kpi-label">Ad Server Cost</span>
            </div>
            <div className="kpi-value text-rose-700">{m(summary.ad_server_cost)}</div>
          </div>
        )}
        {hasAds && (
          <div className="kpi-card animate-fade-in" data-testid="kpi-net-revenue">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                <Wallet size={20} className="text-indigo-600" strokeWidth={1.5} />
              </div>
              <span className="kpi-label">Net Revenue</span>
            </div>
            <div className="kpi-value text-indigo-700">{m(summary.net_revenue)}</div>
          </div>
        )}
        {hasAds && (summary.content_partner_payouts || 0) > 0 && (
          <div className="kpi-card animate-fade-in" data-testid="kpi-cp-payouts">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-fuchsia-50 flex items-center justify-center">
                <Radio size={20} className="text-fuchsia-600" strokeWidth={1.5} />
              </div>
              <span className="kpi-label">SLCP Payouts</span>
            </div>
            <div className="kpi-value text-fuchsia-700">{m(summary.content_partner_payouts)}</div>
          </div>
        )}
        <div className="kpi-card animate-fade-in animate-fade-in-delay-1" data-testid="kpi-admin-earnings">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
              <TrendingUp size={20} className="text-emerald-600" strokeWidth={1.5} />
            </div>
            <span className="kpi-label">Your Earnings</span>
          </div>
          <div className="kpi-value text-emerald-700">{m(summary.admin_earnings)}</div>
        </div>
        <div className="kpi-card animate-fade-in animate-fade-in-delay-2" data-testid="kpi-partner-earnings">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
              <DollarSign size={20} className="text-amber-600" strokeWidth={1.5} />
            </div>
            <span className="kpi-label">SLDP Payouts</span>
          </div>
          <div className="kpi-value text-amber-700">{m(summary.partner_earnings)}</div>
        </div>
        {hasAds && (summary.content_partner_payouts || 0) > 0 && (
        <div className="kpi-card animate-fade-in animate-fade-in-delay-2" data-testid="kpi-total-partner-payouts">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center">
              <Users size={20} className="text-rose-600" strokeWidth={1.5} />
            </div>
            <span className="kpi-label">Total Partner Payouts</span>
          </div>
          <div className="kpi-value text-rose-700">{m((summary.partner_earnings || 0) + (summary.content_partner_payouts || 0))}</div>
        </div>
        )}
        {(hasPeers && !isAdsOnly) && (
        <div className="kpi-card animate-fade-in animate-fade-in-delay-3" data-testid="kpi-total-peers">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Users size={20} className="text-blue-600" strokeWidth={1.5} />
            </div>
            <span className="kpi-label">Avg Active Users</span>
          </div>
          <div className="kpi-value">{summary.avg_peers?.toLocaleString() || "0"}</div>
        </div>
        )}
        {hasAds && (
          <div className="kpi-card animate-fade-in animate-fade-in-delay-3" data-testid="kpi-total-impressions">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <BarChart2 size={20} className="text-purple-600" strokeWidth={1.5} />
              </div>
              <span className="kpi-label">Total Impressions</span>
            </div>
            <div className="kpi-value text-purple-700">{summary.total_impressions?.toLocaleString() || "0"}</div>
          </div>
        )}
      </div>

      {/* Ad Server Cost Not Configured Warning */}
      {data?.cost_warnings?.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-3" data-testid="cost-warnings">
          <AlertTriangle size={18} className="text-rose-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-rose-800">Ad Server Cost Not Configured</p>
            <p className="text-xs text-rose-600 mt-1">
              Net revenue and revenue share cannot be finalized for the periods below until a cost slab is added. These earnings are excluded from Net Revenue / payouts (never assumed as $0).
            </p>
            <ul className="mt-2 space-y-1">
              {data.cost_warnings.map((w, i) => (
                <li key={i} className="text-xs text-rose-700">• {w}</li>
              ))}
            </ul>
            <Link to={costPath} className="inline-block mt-2 text-xs font-semibold text-rose-700 underline hover:text-rose-900" data-testid="cost-warning-link" style={isProduct ? { display: "none" } : undefined}>
              Configure Ad Server Costs →
            </Link>
          </div>
        </div>
      )}

      {/* Unmapped Content Partner Warning */}
      {data?.cp_warnings?.length > 0 && (
        <div className="bg-fuchsia-50 border border-fuchsia-200 rounded-xl p-4 flex items-start gap-3" data-testid="cp-warnings">
          <AlertTriangle size={18} className="text-fuchsia-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-fuchsia-800">{data.cp_warnings.length} Aniview Publisher{data.cp_warnings.length > 1 ? "s" : ""} Not Mapped to a SilverLine Content Partner</p>
            <p className="text-xs text-fuchsia-600 mt-1">
              Revenue for these publishers is held (excluded from payouts) until you map them to a SilverLine Content Partner. Earnings are never assumed.
            </p>
            <ul className="mt-2 space-y-1">
              {data.cp_warnings.map((w, i) => (
                <li key={i} className="text-xs text-fuchsia-700 font-mono">• {w}</li>
              ))}
            </ul>
            <Link to={cpPath} className="inline-block mt-2 text-xs font-semibold text-fuchsia-700 underline hover:text-fuchsia-900" data-testid="cp-warning-link" style={isProduct ? { display: "none" } : undefined}>
              Manage SilverLine Content Partners →
            </Link>
          </div>
        </div>
      )}

      {/* Unattributed Revenue Analysis */}
      {unattributed && unattributed.total_unattributed_earnings > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden" data-testid="unattributed-panel">
          <button
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-100 transition-colors"
            onClick={() => setUnattributedExpanded(v => !v)}
            data-testid="unattributed-toggle"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-slate-500" />
              <span className="text-sm font-semibold text-slate-700">
                Unattributed Revenue — ${unattributed.total_unattributed_earnings.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <span className="text-xs text-slate-400">
                ({unattributed.by_source?.length || 0} source{(unattributed.by_source?.length || 0) !== 1 ? "s" : ""},
                {" "}{unattributed.unmapped_identifiers?.length || 0} unmapped SLID{(unattributed.unmapped_identifiers?.length || 0) !== 1 ? "s" : ""})
              </span>
            </div>
            <ChevronDown size={16} className={`text-slate-400 transition-transform ${unattributedExpanded ? "rotate-180" : ""}`} />
          </button>

          {unattributedExpanded && (
            <div className="border-t border-slate-200 p-4 space-y-4" data-testid="unattributed-detail">
              {/* By Source */}
              {(unattributed.by_source || []).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">By Source</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {unattributed.by_source.map(s => (
                      <div key={s.source} className="bg-white border border-slate-200 rounded-lg px-3 py-2.5" data-testid={`unattributed-source-${s.source}`}>
                        <p className="text-xs text-slate-500">{s.source === "fast_tv_ads" ? "Aniview (FAST TV Ads)" : s.source === "springserve" ? "SpringServe" : "Web-indexing"}</p>
                        <p className="text-sm font-semibold text-slate-800 mt-0.5">${s.earnings.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="text-xs font-normal text-slate-400">({s.rows} rows)</span></p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Unmapped Identifiers */}
              {(unattributed.unmapped_identifiers || []).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Unmapped SLIDs / Identifiers <span className="font-normal text-slate-400">(appear in ad server data but match no company)</span></p>
                  <div className="flex flex-wrap gap-2">
                    {unattributed.unmapped_identifiers.map(i => (
                      <span key={i.identifier} className="inline-flex items-center gap-1 font-mono text-xs px-2 py-1 rounded-md bg-orange-50 text-orange-700 border border-orange-200" data-testid={`unmapped-id-${i.identifier}`}>
                        {i.identifier} <span className="text-orange-400 font-normal">(${i.earnings.toFixed(2)})</span>
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 mt-1.5">
                    Map these to a company via User Management → Companies → Attribution Keys, or update the player to use the company's SLID.
                  </p>
                </div>
              )}

              {/* Unmapped Publishers */}
              {(unattributed.unmapped_publishers || []).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Unmapped Aniview Publishers <span className="font-normal text-slate-400">(not mapped to any SilverLine Content Partner)</span></p>
                  <div className="flex flex-wrap gap-2">
                    {unattributed.unmapped_publishers.map(p => (
                      <span key={p.publisher} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200" data-testid={`unmapped-pub-${p.publisher}`}>
                        {p.publisher} <span className="text-fuchsia-400 font-normal">(${p.earnings.toFixed(2)})</span>
                      </span>
                    ))}
                  </div>
                  <Link to={cpPath} className="inline-block mt-2 text-xs font-semibold text-fuchsia-700 underline hover:text-fuchsia-900" style={isProduct ? { display: "none" } : undefined}>
                    Map to SilverLine Content Partners →
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Share Warnings */}
      {data?.share_warnings?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="share-warnings">
          <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Revenue Share Exceeds 100%</p>
            <p className="text-xs text-amber-600 mt-1">Combined partner shares exceed 100% on some impressions. Payouts have been proportionally capped. Adjust in User Management.</p>
            <ul className="mt-2 space-y-1">
              {data.share_warnings.map((w, i) => (
                <li key={i} className="text-xs text-amber-700">• {w}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* View Toggle + Chart Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode("chart")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === "chart" ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            data-testid="view-chart-btn"
          >
            <BarChart2 size={16} /> Chart
          </button>
          <button
            onClick={() => setViewMode("table")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === "table" ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            data-testid="view-table-btn"
          >
            <Table2 size={16} /> Table
          </button>
        </div>

        {viewMode === "chart" && (
          <div className="flex items-center gap-3">
            {/* Time Granularity */}
            <div className="flex items-center bg-slate-100 rounded-lg p-0.5" data-testid="granularity-toggle">
              {[["day", "Day"], ["week", "Week"], ["month", "Month"]].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setGranularity(val)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${granularity === val ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                  data-testid={`granularity-${val}`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Breakdown Selector */}
            <Select value={breakdown} onValueChange={setBreakdown}>
              <SelectTrigger className="w-[160px] h-8 border-slate-200 text-xs" data-testid="breakdown-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="total">Total</SelectItem>
                <SelectItem value="payout">Payout</SelectItem>
                <SelectItem value="campaign">By Identifier</SelectItem>
                <SelectItem value="partner">By Partner</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Chart View */}
      {viewMode === "chart" && (
        <div className="space-y-4">
          <div className={`grid grid-cols-1 ${!isAdsOnly && !isDataOnly ? 'lg:grid-cols-2' : ''} gap-6`}>
            {/* Revenue Chart */}
            <div className="chart-container" data-testid="earnings-chart">
              <h3 className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Revenue Breakdown
                {granularity !== "day" && <span className="text-xs font-normal text-slate-400 ml-2">({granularity === "week" ? "Weekly" : "Monthly"})</span>}
              </h3>
              <div className="h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  {(breakdown === "total" || breakdown === "payout") ? (
                    <AreaChart data={revenueChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => `$${v}`} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} formatter={(value) => [`$${value.toFixed(2)}`, undefined]} />
                      <Legend />
                      {revenueEntities.map((entity, i) => (
                        <Area key={entity} type="monotone" dataKey={entity} stackId="1" stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} fillOpacity={0.3} />
                      ))}
                    </AreaChart>
                  ) : (
                    <LineChart data={revenueChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => `$${v}`} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} formatter={(value) => [`$${Number(value).toFixed(2)}`, undefined]} />
                      <Legend />
                      {revenueEntities.map((entity, i) => (
                        <Line key={entity} type="monotone" dataKey={entity} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
                      ))}
                    </LineChart>
                  )}
                </ResponsiveContainer>
              </div>
            </div>

            {/* DAU Chart — hidden when ads-only */}
            {!isAdsOnly && (
            <div className="chart-container" data-testid="users-chart">
              <h3 className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {granularity === "day" ? "Daily Active Users" : granularity === "week" ? "Avg Daily Active Users (Weekly)" : "Avg Daily Active Users (Monthly)"}
              </h3>
              <div className="h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  {dauEntities.length <= 1 ? (
                    <BarChart data={dauChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => v.toLocaleString()} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                      <Bar dataKey={dauEntities[0] || "Active Users"} fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  ) : (
                    <LineChart data={dauChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => v.toLocaleString()} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                      <Legend />
                      {dauEntities.map((entity, i) => (
                        <Line key={entity} type="monotone" dataKey={entity} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
                      ))}
                    </LineChart>
                  )}
                </ResponsiveContainer>
              </div>
            </div>
            )}
          </div>

          {/* Impressions Chart — hidden when data-only */}
          {!isDataOnly && hasImpressions && impressionsChartData.length > 0 && (
            <div className="chart-container" data-testid="impressions-chart">
              <h3 className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Ad Impressions
                {granularity !== "day" && <span className="text-xs font-normal text-slate-400 ml-2">({granularity === "week" ? "Weekly Avg" : "Monthly Avg"})</span>}
              </h3>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={impressionsChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => v.toLocaleString()} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} formatter={(value) => [Number(value).toLocaleString(), "Impressions"]} />
                    <Bar dataKey="Impressions" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Table View */}
      {viewMode === "table" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">{tableData.length} records</span>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs border-slate-200"
              data-testid="export-csv-btn"
              onClick={async () => {
                try {
                  const params = {};
                  if (startDate) params.start_date = format(startDate, "yyyy-MM-dd");
                  if (endDate) params.end_date = format(endDate, "yyyy-MM-dd");
                  if (campaignFilter && campaignFilter !== "all") params.campaign_id = campaignFilter;
                  if (sourceFilter && sourceFilter !== "all") params.source = sourceFilter;
                  if (appFilter && appFilter !== "all") params.app_uuid = appFilter;
                  const res = await api.exportCsv(params);
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `earnings_export_${format(new Date(), "yyyyMMdd")}.csv`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                  toast.success("CSV exported");
                } catch { toast.error("Export failed"); }
              }}
            >
              <Download size={14} className="mr-1.5" /> Export CSV
            </Button>
          </div>
          <div className="data-table-container" data-testid="earnings-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Date</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Identifier</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Partner</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Source</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Model</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3 text-right">Raw Earnings</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3 text-right">Your Share</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3 text-right">Partner Share</TableHead>
                  {!isAdsOnly && <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3 text-right">Peers</TableHead>}
                  {hasImpressions && !isDataOnly && <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3 text-right">Impressions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {tableData.length === 0 ? (
                  <TableRow><TableCell colSpan={10} className="text-center py-12 text-slate-400">No data available. Upload data first.</TableCell></TableRow>
                ) : (
                  tableData.slice(tablePage * TABLE_PAGE_SIZE, (tablePage + 1) * TABLE_PAGE_SIZE).map((row, i) => (
                    <TableRow key={i} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <TableCell className="py-2 px-3 text-sm text-slate-700">{row.ymd}</TableCell>
                      <TableCell className="py-2 px-3 text-sm font-medium text-slate-900">{row.campaign}</TableCell>
                      <TableCell className="py-2 px-3 text-sm text-slate-600">{row.partner_name}</TableCell>
                      <TableCell className="py-2 px-3">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${row.source === "fast_tv_ads" ? "bg-purple-50 text-purple-600" : row.source === "springserve" ? "bg-orange-50 text-orange-600" : "bg-sky-50 text-sky-600"}`}>
                          {row.source === "fast_tv_ads" ? "Aniview" : row.source === "springserve" ? "SpringServe" : "Data"}
                        </span>
                      </TableCell>
                      <TableCell className="py-2 px-3">
                        <span className={`model-badge ${row.business_model?.replaceAll("_", "-")}`}>
                          {row.business_model === "full_passback" ? "Full Passback" : row.business_model === "percentage_share" ? "% Share" : row.business_model === "fixed_payout" ? "Fixed" : row.business_model === "cost_not_set" ? "Cost not set" : row.business_model === "cp_not_mapped" ? "Publisher not mapped" : row.business_model === "redacted" ? "—" : row.business_model}
                        </span>
                      </TableCell>
                      <TableCell className="py-2 px-3 text-sm text-right font-mono text-slate-700">{money(row.raw_earnings)}</TableCell>
                      <TableCell className="py-2 px-3 text-sm text-right font-mono text-emerald-700">{money(row.admin_earnings)}</TableCell>
                      <TableCell className="py-2 px-3 text-sm text-right font-mono text-amber-700">{money(row.partner_earnings)}</TableCell>
                      {!isAdsOnly && <TableCell className="py-2 px-3 text-sm text-right font-mono text-slate-700">{row.peers?.toLocaleString()}</TableCell>}
                      {hasImpressions && !isDataOnly && <TableCell className="py-2 px-3 text-sm text-right font-mono text-purple-700">{(row.impressions || 0).toLocaleString()}</TableCell>}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {/* Pagination */}
          {tableData.length > TABLE_PAGE_SIZE && (
            <div className="flex items-center justify-between" data-testid="table-pagination">
              <span className="text-xs text-slate-500">
                {tablePage * TABLE_PAGE_SIZE + 1}–{Math.min((tablePage + 1) * TABLE_PAGE_SIZE, tableData.length)} of {tableData.length}
              </span>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={tablePage === 0} onClick={() => setTablePage(tablePage - 1)} className="h-8 w-8 p-0" data-testid="table-prev">
                  <ChevronLeft size={16} />
                </Button>
                <span className="text-xs text-slate-600 px-2">{tablePage + 1} / {Math.ceil(tableData.length / TABLE_PAGE_SIZE)}</span>
                <Button variant="outline" size="sm" disabled={(tablePage + 1) * TABLE_PAGE_SIZE >= tableData.length} onClick={() => setTablePage(tablePage + 1)} className="h-8 w-8 p-0" data-testid="table-next">
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Campaign Summary */}
      {data?.campaigns?.length > 0 && (
        <div className="data-table-container" data-testid="campaign-summary">
          <div className="p-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Identifier Summary</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Identifier</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Partner</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3 text-right">Total Earnings</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3 text-right">Your Share</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3 text-right">Partner Share</TableHead>
                {!isAdsOnly && <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3 text-right">Total Peers</TableHead>}
                {hasImpressions && !isDataOnly && <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3 text-right">Impressions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.campaigns.map((c, i) => (
                <TableRow key={i} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <TableCell className="py-2 px-3 text-sm font-medium text-slate-900">{c.campaign}</TableCell>
                  <TableCell className="py-2 px-3 text-sm text-slate-600">{c.partner_name}</TableCell>
                  <TableCell className="py-2 px-3 text-sm text-right font-mono text-slate-700">${c.earnings?.toFixed(2)}</TableCell>
                  <TableCell className="py-2 px-3 text-sm text-right font-mono text-emerald-700">${c.admin_earnings?.toFixed(2)}</TableCell>
                  <TableCell className="py-2 px-3 text-sm text-right font-mono text-amber-700">${c.partner_earnings?.toFixed(2)}</TableCell>
                  {!isAdsOnly && <TableCell className="py-2 px-3 text-sm text-right font-mono text-slate-700">{c.peers?.toLocaleString()}</TableCell>}
                  {hasImpressions && !isDataOnly && <TableCell className="py-2 px-3 text-sm text-right font-mono text-purple-700">{(c.impressions || 0).toLocaleString()}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
